import { DataGridPremium, GridRowEditStopReasons, useGridApiRef } from '@mui/x-data-grid-premium';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useTRPC } from '#src/trpc.js';
import { ManufacturerTableToolbar } from './manufacturer-table-toolbar.js';

import type { HilManufacturer } from '@cadstrom/backend/shared';
import type { DataGridPremiumProps, GridColDef, GridEventListener } from '@mui/x-data-grid-premium';
import type { ManufacturerTableToolbarProps } from './manufacturer-table-toolbar.js';

export const Route = createFileRoute('/_authorization-gate/manufacturers/')({
	staticData: {
		authorize: (ability) => ability.can('manage', 'manufacturer'),
		navigation: {
			label: 'Manufacturer Index',
			keywords: ['manufacturers', 'mfr'],
		},
	},
	beforeLoad: ({ context: { trpc } }) => {
		return { getManufacturersOptions: trpc.manufacturer.getMany.queryOptions({}) };
	},
	loader: async ({ context: { queryClient, getManufacturersOptions } }) => {
		await queryClient.fetchQuery(getManufacturersOptions);
	},
	head: () => ({ meta: [{ title: 'Manufacturer Index • Cadstrom' }] }),
	component: ManufacturerIndex,
});

function ManufacturerIndex() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const apiRef = useGridApiRef();

	const { getManufacturersOptions } = Route.useRouteContext();
	const [draftRow, setDraftRow] = useState<HilManufacturer | null>(null);

	const {
		data: { manufacturers },
	} = useSuspenseQuery(getManufacturersOptions);

	const { mutateAsync: createManufacturer } = useMutation(
		trpc.manufacturer.create.mutationOptions({
			onSuccess: ({ manufacturer }) => {
				queryClient.setQueryData(getManufacturersOptions.queryKey, (data) => ({
					manufacturers: [...(data?.manufacturers ?? []), manufacturer],
				}));
			},
		}),
	);
	const { mutateAsync: updateManufacturer } = useMutation(
		trpc.manufacturer.update.mutationOptions({
			onSuccess: ({ manufacturer }) => {
				queryClient.setQueryData(getManufacturersOptions.queryKey, (data) => ({
					manufacturers: (data?.manufacturers ?? []).map((m) => (m.id === manufacturer.id ? manufacturer : m)),
				}));
			},
		}),
	);

	const columns: Array<GridColDef<HilManufacturer>> = useMemo(
		() => [
			{
				field: 'name',
				headerName: 'Name',
				width: 400,
				editable: true,
			} satisfies GridColDef<HilManufacturer, HilManufacturer['name']>,
			{
				field: 'id',
				headerName: 'ID',
				flex: 1,
			} satisfies GridColDef<HilManufacturer, HilManufacturer['id']>,
		],
		[],
	);

	const rows = useMemo(() => {
		const rows = [...manufacturers];

		if (draftRow) {
			rows.push(draftRow);
		}

		return rows;
	}, [draftRow, manufacturers]);

	const handleAddDraftRow = useCallback(() => {
		setDraftRow({
			id: 'draft',
			name: '',
		});
	}, []);

	// Start editing the draft row once it is added
	useEffect(() => {
		if (!apiRef.current) {
			return;
		}

		if (draftRow) {
			apiRef.current.startRowEditMode({
				id: 'draft',
				fieldToFocus: 'name',
			});
		}
	}, [apiRef, draftRow]);

	const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
		if (params.id === 'draft') {
			if (params.reason === GridRowEditStopReasons.rowFocusOut) {
				event.defaultMuiPrevented = true;
			} else {
				setDraftRow(null);
			}
		}
	};

	const processRowUpdate = async (newRow: HilManufacturer, oldRow: HilManufacturer) => {
		if (oldRow.id === 'draft') {
			await createManufacturer({
				name: newRow.name,
			});
			setDraftRow(null);
			return newRow;
		} else {
			await updateManufacturer({
				id: oldRow.id,
				data: {
					name: newRow.name,
				},
			});
			return newRow;
		}
	};

	const slots: DataGridPremiumProps['slots'] = useMemo(
		() => ({
			toolbar: ManufacturerTableToolbar,
		}),
		[],
	);

	const slotProps: DataGridPremiumProps['slotProps'] = useMemo(
		() => ({
			toolbar: {
				numManufacturers: manufacturers.length,
				onClickNewManufacturer: handleAddDraftRow,
			},
		}),
		[manufacturers.length, handleAddDraftRow],
	);

	return (
		<div className='flex min-h-0 grow flex-col'>
			<div className='flex min-h-0 grow flex-col px-3 py-2'>
				<DataGridPremium
					apiRef={apiRef}
					columns={columns}
					rows={rows}
					editMode='row'
					showToolbar
					slots={slots}
					slotProps={slotProps}
					onRowEditStop={handleRowEditStop}
					processRowUpdate={processRowUpdate}
					onProcessRowUpdateError={(error) => {
						throw error;
					}}
				/>
			</div>
		</div>
	);
}

declare module '@mui/x-data-grid-premium' {
	interface ToolbarPropsOverrides extends ManufacturerTableToolbarProps {}
}
